import { StateScreen, ContactLine } from "./StateScreen";
import { AcceptedBreakdown } from "./AcceptedBreakdown";
import { formatDate } from "@/lib/format";
import { formatMoney } from "@/lib/money";
import type { Currency, LineItem, VatTreatment } from "@/types";

/**
 * The terminal / not-found public screens. The `live` state is handled by
 * PublicQuotePage (it renders the block document). `converted` is never a
 * public state — the server resolves it to `accepted` (build correction #1),
 * so AcceptedScreen covers both.
 */

export function AcceptedScreen({
  quoteNumber,
  acceptedTotal,
  currency,
  vatTreatment,
  lineItems,
  selectedOptionalIds,
  shortDescription,
}: {
  quoteNumber?: string;
  /** Authoritative accepted figure snapshotted by the server, if known. */
  acceptedTotal?: number;
  currency?: Currency;
  vatTreatment?: VatTreatment;
  /** When present, the agreed breakdown is rendered. */
  lineItems?: LineItem[];
  selectedOptionalIds?: number[];
  /** Fills the `{{short_description}}` token in the breakdown's line items. */
  shortDescription?: string;
}) {
  // Also used for `converted` (treated the same on the public page).
  const hasBreakdown = !!lineItems && lineItems.length > 0;
  return (
    <StateScreen
      tone="success"
      eyebrow="Thank you"
      title="This quote has been accepted"
      quoteNumber={quoteNumber}
      wide={hasBreakdown}
    >
      <p>
        We’ve recorded your acceptance and the team has been notified. We’ll be in touch
        shortly with next steps to get your project underway.
      </p>
      {hasBreakdown ? (
        <AcceptedBreakdown
          lineItems={lineItems}
          selectedOptionalIds={selectedOptionalIds}
          currency={currency}
          vatTreatment={vatTreatment}
          acceptedTotal={acceptedTotal}
          shortDescription={shortDescription}
        />
      ) : typeof acceptedTotal === "number" ? (
        <p className="state-screen__total">
          Accepted total: <strong className="numeric">{formatMoney(acceptedTotal, currency)}</strong>
        </p>
      ) : null}
      <ContactLine />
    </StateScreen>
  );
}

export function DeclinedScreen({ quoteNumber }: { quoteNumber?: string }) {
  return (
    <StateScreen tone="danger" eyebrow="Recorded" title="This quote has been declined" quoteNumber={quoteNumber}>
      <p>
        Thanks for letting us know. If anything changes, or you’d like us to revise the
        proposal, we’d be glad to talk it through.
      </p>
      <ContactLine />
    </StateScreen>
  );
}

export function WithdrawnScreen({ quoteNumber }: { quoteNumber?: string }) {
  return (
    <StateScreen tone="neutral" eyebrow="No longer available" title="This quote has been withdrawn" quoteNumber={quoteNumber}>
      <p>
        This proposal has been withdrawn and is no longer active. If you were expecting to
        review it, please get in touch and we’ll send an updated version.
      </p>
      <ContactLine />
    </StateScreen>
  );
}

export function ExpiredScreen({
  quoteNumber,
  expiryDate,
}: {
  quoteNumber?: string;
  expiryDate?: string;
}) {
  // Expired uses the danger palette deliberately (no --warning in components).
  return (
    <StateScreen tone="danger" eyebrow="Expired" title="This quote has expired" quoteNumber={quoteNumber}>
      <p>
        This proposal was valid until {formatDate(expiryDate)} and has now expired. Quotes are
        time-limited so pricing stays accurate — we’d be happy to issue a fresh one.
      </p>
      <ContactLine />
    </StateScreen>
  );
}

export function NotFoundScreen() {
  return (
    <StateScreen tone="info" eyebrow="Not found" title="We couldn’t find that quote">
      <p>
        This link doesn’t match a quote we have on record. Please check the address, or use the
        most recent link we sent you.
      </p>
      <ContactLine />
    </StateScreen>
  );
}

export function ErrorScreen({ message }: { message?: string }) {
  return (
    <StateScreen tone="danger" eyebrow="Something went wrong" title="We couldn’t load this quote">
      <p>{message || "Please try again in a moment."}</p>
      <ContactLine />
    </StateScreen>
  );
}
